import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiUser, FiPhone, FiMail, FiCalendar, FiDollarSign } from 'react-icons/fi';
import { toast } from 'react-toastify';
import technicianService from '../services/technicianService';
import Button from '../components/Button';
import './DetailView.css';

const TechnicianView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [technician, setTechnician] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTechnician();
  }, [id]);

  const fetchTechnician = async () => {
    try {
      const response = await technicianService.getById(id);
      setTechnician(response.data);
    } catch (error) {
      toast.error('Failed to fetch technician details');
      navigate('/technicians');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!technician) {
    return null;
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      available: 'badge-success',
      on_site: 'badge-primary',
      off_duty: 'badge-gray',
      on_leave: 'badge-warning',
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  return (
    <div className="detail-view-container">
      <div className="detail-header">
        <button onClick={() => navigate('/technicians')} className="back-button">
          <FiArrowLeft /> Back to List
        </button>
        <Link to={`/technicians/${id}/edit`}>
          <Button variant="primary" icon={<FiEdit />}>
            Edit Technician
          </Button>
        </Link>
      </div>

      <div className="detail-card">
        <div className="detail-hero">
          <div className="hero-avatar">
            <FiUser />
          </div>
          <div className="hero-info">
            <h1 className="hero-title">{technician.name}</h1>
            <p className="hero-subtitle">Employee ID: {technician.employeeId}</p>
            <span className={getStatusBadge(technician.status)}>
              {technician.status.replace('_', ' ')}
            </span>
          </div>
        </div>

        <div className="detail-sections">
          <div className="detail-section">
            <h3 className="section-title">Contact Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <FiPhone className="info-icon" />
                <div>
                  <span className="info-label">Phone</span>
                  <span className="info-value">{technician.phone}</span>
                </div>
              </div>
              {technician.email && (
                <div className="info-item">
                  <FiMail className="info-icon" />
                  <div>
                    <span className="info-label">Email</span>
                    <span className="info-value">{technician.email}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3 className="section-title">Professional Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <FiCalendar className="info-icon" />
                <div>
                  <span className="info-label">Experience</span>
                  <span className="info-value">{technician.experience || 0} years</span>
                </div>
              </div>
              <div className="info-item">
                <FiDollarSign className="info-icon" />
                <div>
                  <span className="info-label">Monthly Salary</span>
                  <span className="info-value">₹{technician.salary?.toLocaleString() || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <FiCalendar className="info-icon" />
                <div>
                  <span className="info-label">Joining Date</span>
                  <span className="info-value">
                    {new Date(technician.joiningDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {technician.expertise && technician.expertise.length > 0 && (
            <div className="detail-section">
              <h3 className="section-title">Areas of Expertise</h3>
              <div className="expertise-tags">
                {technician.expertise.map((skill, idx) => (
                  <span key={idx} className="expertise-tag">{skill}</span>
                ))}
              </div>
            </div>
          )}

          {technician.certifications && technician.certifications.length > 0 && (
            <div className="detail-section">
              <h3 className="section-title">Certifications</h3>
              <div className="certifications-list">
                {technician.certifications.map((cert, idx) => (
                  <div key={idx} className="certification-item">
                    <h4>{cert.name}</h4>
                    <div className="cert-dates">
                      <span>Issued: {new Date(cert.issueDate).toLocaleDateString()}</span>
                      {cert.expiryDate && (
                        <span>Expires: {new Date(cert.expiryDate).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicianView;
